// SPDX-License-Identifier: LicenseRef-Proprietary
import {
  REGION_STATIC_DATA,
  getRegionPopulation,
  getNationalPopulation,
} from '../../../data/regions';
import type { PopulationMode } from '../../../data/regions';
import type { TreeNode, TreeEdge } from '../../../lib/validation';

/**
 * Health Economics Calculation Engine for Vaccine Value Demo
 * Pure functions for calculating vaccine program outcomes
 */

export interface Citations {
  vaccineEfficacy?: string;
  baselineIncidence?: string;
  vaccineCost?: string;
  treatmentCost?: string;
  productivityValue?: string;
  durationInfection?: string;
  utilityInfection?: string;
  hospitalizationRate?: string;
  hospitalizationCost?: string;
  durationHospitalization?: string;
  mortalityRate?: string;
  waning?: string;
  adminCost?: string;
  adverseEventRate?: string;
  adverseEventCost?: string;
  adverseEventQalyLoss?: string;
}

export interface ComplicationCategory {
  id: string;                   // stable id; key for React lists & save/load
  name: string;                 // user-editable label, e.g. "Hospitalization"
  enabled: boolean;             // soft-disable without deleting
  incidencePctBands: number[];  // % of infected, per 5-yr age band, length = ceil((H-1)/5)
  costPerCaseBands: number[];   // € per case, per 5-yr age band, same length as incidencePctBands
  citation?: string;
}

export interface VaccineInputs {
  selectedRegions: string[]; // Array of selected region keys
  population: number;
  populationMode?: PopulationMode;
  vaccineName?: string;
  baselineIncidencePer100k: number;
  vaccineEfficacyPct: number;
  waningPctPerYear: number;
  timeHorizonYears: number;
  // Time-horizon resolution mode
  // 'manual' (default): use timeHorizonYears verbatim
  // 'lifetime': derive horizon from cohortStartingAge and the mortality source's terminal age
  //   (requires mortalitySource === 'finnish_lifetable_2023' and cohortStartingAge set)
  horizonMode?: 'manual' | 'lifetime';
  vaccineCostPerDoseEUR: number;
  vaccinePriceDiscountPct: number;
  dosesPerCourse: number;
  adminCostPerDoseEUR: number;
  treatmentCostPerCaseEUR: number;
  productivityLossDaysPerCase: number;
  productivityValuePerDayEUR: number;
  discountRatePct: number;
  defaultCoveragePct: number; // Default coverage for new regions
  coveragePctByRegion: { [regionKey: string]: number };
  perspective: 'healthcare' | 'societal'; // Healthcare vs Societal perspective
  citations?: Citations; // Optional citations for key parameters
  // Adverse Events
  adverseEventRate: number;
  adverseEventCost: number;
  adverseEventQalyLoss: number;
  // Disease Parameters
  durationInfectionDays: number;
  utilityInfection: number;
  utilityHospitalization: number;
  baselineMortalityRate: number;
  hospitalizationRate: number;
  // Hospitalization Parameters
  hospitalizationCostPerCaseEUR: number;
  durationHospitalizationDays: number;
  // Multi-year analysis parameters
  boosterYears: number[]; // Integers in [2..timeHorizonYears]
  boosterCost: number; // ≥ 0
  discountingEnabled: boolean; // Only enabled when timeHorizonYears > 1
  // Vaccine efficacy waning model
  // 'exponential' (default) uses waningPctPerYear; 'schedule' uses veSchedule per year 2..timeHorizonYears
  waningMode?: 'exponential' | 'schedule';
  veSchedule?: number[]; // Length = timeHorizonYears - 1, each value as percent in [0, 100]
  // Background all-cause mortality source
  // 'none' (default): backend receives baseline_mortality: 0 (unchanged behaviour)
  // 'finnish_lifetable_2023': backend resolves per-cycle p_death_bg from Tilastokeskus 2023
  mortalitySource?: 'none' | 'finnish_lifetable_2023';
  // Generic cohort starting age (0-100, integer). Required when mortalitySource === 'finnish_lifetable_2023',
  // incidenceMode === 'schedule', or qalyLossMode === 'schedule'. Shared across age-indexed schedules.
  cohortStartingAge?: number;
  // Incidence input mode
  // 'scalar' (default): backend uses baseline_incidence (current behaviour)
  // 'schedule': frontend expands 5-year age bands -> per-cycle incidence_schedule
  incidenceMode?: 'scalar' | 'schedule';
  incidenceBandsPer100k?: number[]; // Length = ceil((timeHorizonYears - 1) / 5), units /100k
  // Per-case QALY loss input mode
  // 'scalar' (default): backend derives qaly_loss_infection from utilityInfection + durationInfectionDays
  // 'schedule': frontend expands 5-year age bands -> per-cycle qaly_loss_schedule (un-absorbed by duration)
  qalyLossMode?: 'scalar' | 'schedule';
  qalyLossBands?: number[]; // Length = ceil((timeHorizonYears - 1) / 5), values are QALY loss per case
  // User-definable complication categories. Each entry contributes
  //   p_inf * incidencePctBands[band]/100 * costPerCaseBands[band] €
  // to the per-cycle alive-state cost. Bands are 5-year, aligned with
  // incidenceBandsPer100k/qalyLossBands (length = ceil((H-1)/5)).
  complications?: ComplicationCategory[];
  // Background utility (healthy-state HRQoL) source
  // 'none' (default): backend leaves per-cycle healthy-state utility at 1.0 (unchanged behaviour)
  // other values: registry preset id; backend resolves per-cycle background_utility_schedule
  backgroundUtilitySource?: string;
}

// Population data for each region (approximate 2023 figures)
export interface VaccineOutputs {
  // Strategy-specific outcomes
  vaccinate: {
    lifeYears: number;
    qalys: number;
    deaths: number;
    cases: number;
    hospitalizations: number;
    programmeCost: number;
    healthcareCost: number;
    indirectCost: number;
    totalCost: number;
  };
  noVaccinate: {
    lifeYears: number;
    qalys: number;
    deaths: number;
    cases: number;
    hospitalizations: number;
    programmeCost: number;
    healthcareCost: number;
    indirectCost: number;
    totalCost: number;
  };
  // Incremental outcomes (Δ = Vaccinate - No Vaccinate)
  incremental: {
    lifeYearsGained: number;
    qalyGained: number;
    deathsAverted: number;
    casesAvoided: number;
    hospitalizationsAvoided: number;
    costDifference: number; // ΔC
  };
  // Economic evaluation metrics
  icer: number; // ΔC / ΔQ (EUR per QALY gained)
  bcr: number; // Benefit-cost ratio (Cost savings / Programme cost)
  budgetImpact: number; // C_vaccination - Cost savings
  costBreakdown: {
    directMedicalCost: number;
    programmeDeliveryCost: number;
    productivityCost: number;
    adverseEventCost: number;
  };
  // Legacy fields for backward compatibility
  avoidedCases: number;
  avoidedHosp: number;
  qalyGained: number;
  totalSavings: number;
  netSavings: number;
  vaccineCosts: number;
  casesCostSaved: number;
  productivitySaved: number;
  doses: number;
  series: TimeSeriesData[];
  pieData: PieData[];
  regional: RegionalMetric[];
  waterfall: WaterfallData;
  // Multi-year analysis outputs
  veByYear?: number[]; // VE values for years 1..H
}

export const EMPTY_VACCINE_OUTPUTS: VaccineOutputs = {
  vaccinate: {
    lifeYears: 0, qalys: 0, deaths: 0, cases: 0, hospitalizations: 0,
    programmeCost: 0, healthcareCost: 0, indirectCost: 0, totalCost: 0
  },
  noVaccinate: {
    lifeYears: 0, qalys: 0, deaths: 0, cases: 0, hospitalizations: 0,
    programmeCost: 0, healthcareCost: 0, indirectCost: 0, totalCost: 0
  },
  incremental: {
    lifeYearsGained: 0, qalyGained: 0, deathsAverted: 0, casesAvoided: 0,
    hospitalizationsAvoided: 0, costDifference: 0
  },
  icer: 0, bcr: 0, budgetImpact: 0,
  costBreakdown: {
    directMedicalCost: 0,
    programmeDeliveryCost: 0,
    productivityCost: 0,
    adverseEventCost: 0,
  },
  avoidedCases: 0, avoidedHosp: 0, qalyGained: 0, totalSavings: 0, netSavings: 0,
  vaccineCosts: 0, casesCostSaved: 0, productivitySaved: 0, doses: 0,
  series: [],
  pieData: [],
  regional: [],
  waterfall: {
    treatmentSavings: 0,
    productivitySavings: 0,
    adverseEventCost: 0,
    programmeCost: 0
  }
};

export interface RegionalMetric {
  id: string;
  name: string;
  population: number;
  coverage: number;
  casesPrevented: number;
  hospitalizationsPrevented: number;
  lifeYearsGained: number;
  costImpact: number;
}

export interface WaterfallData {
  treatmentSavings: number;
  productivitySavings: number;
  adverseEventCost: number;
  programmeCost: number;
}

export interface TimeSeriesData {
  year: string | number;
  baselineCases: number;
  withVaccineCases: number;
  avoidedCases: number;
  savingsEUR: number;
  costWithVaccine?: number;
  costWithoutVaccine?: number;
  netCost?: number;
  cumulativeNetCost?: number;
  programmeCost?: number;
  // Multi-year analysis fields (optional for backward compatibility)
  vaccineEfficacy?: number; // VE value for this year
  qalysWithVaccine?: number; // Undiscounted QALYs for vaccinated group
  qalysWithoutVaccine?: number; // Undiscounted QALYs for unvaccinated group
  qalysWithVaccineDiscounted?: number; // qalysWithVaccine * discountFactor; sums to headline cumulative discounted QALYs
  qalysWithoutVaccineDiscounted?: number;
  discountFactor?: number; // Per-cycle start-of-year factor: 1/(1+r)^(year-1); 1 when discounting disabled or H==1
  aliveVac?: number; // Cohort alive at start of cycle, vaccinate arm
  aliveNovac?: number; // Cohort alive at start of cycle, no-vaccine arm
  backgroundMortality?: number; // Per-cycle background all-cause mortality (p_death_bg), shared across arms
  backgroundUtility?: number; // Per-cycle general-population HRQoL multiplier (bg_util) applied to healthy-state utility; shared across arms. NA for Y1 when Y1 comes from the decision tree.
  bgDeathsVac?: number;
  bgDeathsNovac?: number;
  deathsVac?: number;
  deathsNovac?: number;
}

export interface PieData {
  name: string;
  value: number;
}



export interface Scenario {
  id: string;
  name: string;
  audience: string;
  disease: string;
  inputs: VaccineInputs;
  outputs: VaccineOutputs;
  decisionTree?: {
    nodes: Record<string, TreeNode>;
    edges: TreeEdge[];
    rootId: string | null;
  };
  timestamp: number;
  evidenceIds?: string[]; // Array of PMIDs linking to saved evidence
}

export interface EvidenceItem {
  id: string;        // PMID (PubMed ID)
  source: "pubmed";  // Source identifier (extensible for future sources)
  title: string;
  abstract?: string;
  journal?: string;
  year?: number;
  firstAuthor?: string;
  doi?: string;
  url: string;       // PubMed URL (e.g., https://pubmed.ncbi.nlm.nih.gov/{pmid}/)
  addedAt: string;   // ISO 8601 timestamp when added to library
  aiBrief?: AbstractTriageBrief; // AI-generated triage summary (added when triaged)
}

/**
 * AI Abstract Triage Types
 * Defines data contracts for OpenAI-powered PubMed abstract analysis
 * Used by /ai/abstractTriage endpoint
 */

/**
 * Single item in a triage request
 */
export interface AbstractTriageRequestItem {
  pmid: string;           // PubMed ID (numeric string)
  title: string;          // Paper title
  abstract: string;       // Paper abstract (min 50 chars, max 3000 chars truncated server-side)
  year?: number;          // Publication year
  journal?: string;       // Journal name
  pub_types?: string[];   // snake_case for API contract
  keywords?: string[];
  mesh_terms?: string[];  // snake_case for API contract
}

/**
 * Request format for AI abstract triage endpoint
 * Analyzes PubMed abstracts to extract HTA-relevant information
 */
export interface AbstractTriageRequest {
  mode: "hta_brief";
  items: AbstractTriageRequestItem[];
}

/**
 * Potential evidence sources for vaccine HTA model inputs
 * Maps abstract content to relevant vaccine model parameters
 */
export type PotentialInputSource =
  | "vaccineEfficacy"
  | "waningBoosters"
  | "incidenceEpi"
  | "costsResourceUse"
  | "utilitiesQoL"
  | "safety"
  | "coverageAdherence"
  | "modelStructure"
  | "none";

/**
 * AI-generated triage summary for a single abstract
 * Structured output from OpenAI Chat Completions API with json_schema validation
 */
export interface AbstractTriageBrief {
  mode: "hta_brief";
  abstractHash: string;         // SHA-256 hash (64-char hex of normalized+truncated abstract)
  summary: string;              // 1-2 sentence summary of key findings
  studyType: string;            // "RCT", "observational", "systematic review", "unknown", etc.
  potentialInputSources: PotentialInputSource[]; // Mapping to vaccine model parameters
  confidence: "low" | "medium" | "high"; // Confidence in triage accuracy
  cautions: string[];           // Warnings or limitations (e.g., "abstract-only", "limited generalizability")
  generatedAt: string;          // ISO 8601 timestamp when generated
  
  // NEW FIELDS (v2)
  pmid?: string;
  retrievable_inputs?: RetrievableInput[];
  geo_context?: "finland" | "nordic" | "eu" | "other" | "unknown";
  finland_applicability_overall?: FinlandApplicability;
  geo_basis?: Basis | "none";
  geo_supporting_text?: string;
}

// NEW TYPES FOR V2
export type Likelihood = 
  | "explicit_in_abstract"
  | "likely_in_full_text"
  | "uncertain"
  | "unlikely";

export type Basis = "abstract" | "title" | "pub_type" | "mesh";

export type FinlandApplicability = "high" | "medium" | "low" | "unknown";

export interface RetrievableInput {
  input_key: string;
  likelihood: Likelihood;
  basis?: Basis;
  supporting_text?: string;
  finland_applicability?: FinlandApplicability;
  applicability_notes?: string;
}

/**
 * Single triage result item
 */
export interface AbstractTriageItem {
  pmid: string;
  brief: AbstractTriageBrief;
}

/**
 * Response format from AI abstract triage endpoint
 * Contains triaged items and request ID for tracking
 */
export interface AbstractTriageResponse {
  requestId: string;
  data: {
    items: AbstractTriageItem[];
  };
  errors?: Array<{
    pmid: string;
    code: string;
    message: string;
  }>;
}

export interface Region {
  key: string;
  name: string;
}

export const REGIONS: Region[] = REGION_STATIC_DATA.map((region) => ({
  key: region.id,
  name: region.name,
}));

export const DISEASES = [
  { key: "influenza", name: "Influenza" },
  { key: "rsv", name: "RSV" },
  { key: "hpv", name: "HPV" },
  { key: "pneumococcal", name: "Pneumococcal" },
];

export const AUDIENCES = [
  { key: "gov", label: "Government / Finance" },
  { key: "thl", label: "THL / HTA" },
  { key: "clinic", label: "Hospital / Clinic" },
];

export const DEFAULT_INPUTS: VaccineInputs = {
  selectedRegions: ['finland'], // Default to entire Finland
  population: getNationalPopulation('all'),
  populationMode: 'all',
  baselineIncidencePer100k: 1500, // annual cases per 100k
  vaccineEfficacyPct: 65,
  waningPctPerYear: 0, // Default 0 for backward compatibility (no waning)
  timeHorizonYears: 1,
  vaccineCostPerDoseEUR: 25,
  vaccinePriceDiscountPct: 0,
  dosesPerCourse: 1,
  adminCostPerDoseEUR: 5,
  treatmentCostPerCaseEUR: 600,
  productivityLossDaysPerCase: 2,
  productivityValuePerDayEUR: 180,
  discountRatePct: 3,
  defaultCoveragePct: 75, // Default coverage for new regions
  coveragePctByRegion: Object.fromEntries(REGIONS.map(r => [r.key, 75])),
  perspective: 'healthcare', 
  citations: {},
  adverseEventRate: 0.0005,
  adverseEventCost: 200,
  adverseEventQalyLoss: 0.001,
  durationInfectionDays: 7,
  utilityInfection: 0.9,
  utilityHospitalization: 0.7,
  baselineMortalityRate: 0.001,
  hospitalizationRate: 0.05,
  hospitalizationCostPerCaseEUR: 3000,
  durationHospitalizationDays: 5,
  // Multi-year analysis defaults
  boosterYears: [],
  boosterCost: 0,
  discountingEnabled: false, // Disabled for H=1 (backward compatibility)
  mortalitySource: 'none',
  horizonMode: 'manual',
  incidenceMode: 'scalar',
  qalyLossMode: 'scalar',
  complications: [],
  backgroundUtilitySource: 'none',
};



/**
 * Terminal age (inclusive) for each supported mortality source's life table.
 * Used to derive lifetime horizon = LIFETABLE_TERMINAL_AGE - cohortStartingAge + 1.
 * Must agree with the source JSON under
 * R/domain/decision_tree/multi_year/data/mortality_sources/.
 */
export const LIFETABLE_TERMINAL_AGE: Record<NonNullable<VaccineInputs['mortalitySource']>, number | null> = {
  none: null,
  finnish_lifetable_2023: 99,
};

/**
 * Resolve the effective time horizon (in years) the model should run.
 *
 * When `horizonMode === 'lifetime'` AND a supported mortality source is selected
 * AND `cohortStartingAge` is set, returns the remaining lifetime to the life
 * table's terminal age. Otherwise returns `timeHorizonYears` verbatim.
 *
 * Clamped to >= 1 so callers can always assume a valid positive integer.
 */
export function resolveEffectiveTimeHorizon(
  inputs: Pick<VaccineInputs, 'horizonMode' | 'timeHorizonYears' | 'cohortStartingAge' | 'mortalitySource'>,
): number {
  if (inputs.horizonMode === 'lifetime' && typeof inputs.cohortStartingAge === 'number') {
    const terminalAge = inputs.mortalitySource ? LIFETABLE_TERMINAL_AGE[inputs.mortalitySource] : null;
    if (terminalAge != null) {
      return Math.max(1, terminalAge - inputs.cohortStartingAge + 1);
    }
  }
  return Math.max(1, inputs.timeHorizonYears ?? 1);
}

/**
 * Whether lifetime-horizon mode can actually be resolved for the given inputs.
 * Used by the UI to enable/disable the 'lifetime' option.
 */
export function canResolveLifetimeHorizon(
  inputs: Pick<VaccineInputs, 'cohortStartingAge' | 'mortalitySource'>,
): boolean {
  if (typeof inputs.cohortStartingAge !== 'number') return false;
  const terminalAge = inputs.mortalitySource ? LIFETABLE_TERMINAL_AGE[inputs.mortalitySource] : null;
  return terminalAge != null;
}

/**
 * Calculate ICER (Incremental Cost-Effectiveness Ratio)
 */
export function calculateICER(costs: number, qalys: number): number {
  return costs > 0 && qalys > 0 ? costs / qalys : 0;
}

/**
 * Format currency for Finnish locale
 */
export function formatEuros(n: number): string {
  return new Intl.NumberFormat("fi-FI", { 
    style: "currency", 
    currency: "EUR", 
    maximumFractionDigits: 0 
  }).format(n || 0);
}

/**
 * Format currency in compact notation for large numbers
 */
export function formatEurosCompact(n: number): string {
  if (n >= 1_000_000_000) {
    return new Intl.NumberFormat("fi-FI", { 
      style: "currency", 
      currency: "EUR", 
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: 1
    }).format(n || 0);
  }
  return formatEuros(n);
}

/**
 * Format percentage
 */
export function formatPercentage(n: number): string {
  return `${(n ?? 0).toFixed(0)}%`;
}

/**
 * Validate and clamp numeric inputs
 */
export function validateNumericInput(value: number, min: number = 0, max: number = Number.MAX_SAFE_INTEGER): number {
  if (isNaN(value) || !isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/**
 * Validate coverage percentage
 */
export function validateCoverage(value: number): number {
  return validateNumericInput(value, 0, 100);
}

/**
 * Calculate total population for selected regions
 */
export function calculateTotalPopulation(selectedRegions: string[]): number {
  if (selectedRegions.includes('finland')) {
    return getNationalPopulation('all');
  }

  return selectedRegions.reduce((total, regionKey) => {
    return total + getRegionPopulation(regionKey, 'all');
  }, 0);
}

/**
 * Mapping between internal region keys and HVA IDs (numeric)
 * Based on actual Finnish HVA numbering system from map data
 */
export const REGION_TO_HVA_MAP: Record<string, string> = {
  'finland': 'all',
  'ita-uusimaa': '01',        // Itä-Uudenmaan hyvinvointialue
  'keski-uusimaa': '02',      // Keski-Uudenmaan hyvinvointialue
  'lansi-uusimaa': '03',      // Länsi-Uudenmaan hyvinvointialue
  'vantaa-kerava': '04',      // Vantaan ja Keravan hyvinvointialue
  'varsinais-suomi': '05',    // Varsinais-Suomen hyvinvointialue
  'satakunta': '06',          // Satakunnan hyvinvointialue
  'kanta-hame': '07',         // Kanta-Hämeen hyvinvointialue
  'pirkanmaa': '08',          // Pirkanmaan hyvinvointialue
  'paijat-hame': '09',        // Päijät-Hämeen hyvinvointialue
  'kymenlaakso': '10',        // Kymenlaakson hyvinvointialue
  'etela-karjala': '11',      // Etelä-Karjalan hyvinvointialue
  'etela-savo': '12',         // Etelä-Savon hyvinvointialue
  'pohjois-savo': '13',       // Pohjois-Savon hyvinvointialue
  'pohjois-karjala': '14',    // Pohjois-Karjalan hyvinvointialue
  'keski-suomi': '15',        // Keski-Suomen hyvinvointialue
  'etela-pohjanmaa': '16',    // Etelä-Pohjanmaan hyvinvointialue
  'pohjanmaa': '17',          // Pohjanmaan hyvinvointialue
  'keski-pohjanmaa': '18',    // Keski-Pohjanmaan hyvinvointialue
  'pohjois-pohjanmaa': '19',  // Pohjois-Pohjanmaan hyvinvointialue
  'kainuu': '20',             // Kainuun hyvinvointialue
  'lapland': '21',            // Lapin hyvinvointialue
  'helsinki': '90',           // Helsingin kaupunki
  'ahvenanmaa': '91',         // Ahvenanmaa
};

export const HVA_TO_REGION_MAP: Record<string, string> = 
  Object.fromEntries(Object.entries(REGION_TO_HVA_MAP).map(([k, v]) => [v, k]));
